import { Button, CloseIcon, LazyLoader } from '@bp/ui-components';
import classNames from 'classnames';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './StackView.module.scss';

type StackViewProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  zIndex?: number;
};

export const StackView = ({ isOpen, onClose, title, children, zIndex = 80 }: StackViewProps) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleRequestClose = useCallback(() => {
    if (!isClosing) {
      setIsClosing(true);
    }
  }, [isClosing]);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) onClose();
  }, [isClosing, onClose]);

  if (!isOpen) return null;

  const classes = classNames(styles.drawer, {
    [styles['closing']]: isClosing,
  });

  return (
    <div className={styles.overlay} onClick={handleRequestClose} style={{ zIndex }}>
      <div className={classes} onClick={(e) => e.stopPropagation()} onAnimationEnd={handleAnimationEnd}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <Button
            hierarchy='tertiary'
            icon={<CloseIcon />}
            onClick={handleRequestClose}
            aria-label={t('common.close')}
          />
        </div>
        <div className={styles.content}>
          <Suspense fallback={<LazyLoader embedded transparent size='l' forceHeight='45vh' />}>{children}</Suspense>
        </div>
      </div>
    </div>
  );
};
